import React, { useEffect, useCallback, useState } from 'react';
import { PaymentIFrameProps } from './props';
import ReactIFrame from 'react-iframe';
import { MessageType } from '../../models/messages';

const PaymentIFrame: React.FC<PaymentIFrameProps> = (props: PaymentIFrameProps) => {
  const [isPrimaryTab, setIsPrimaryTab] = useState(true);
  const [duplicateDetected, setDuplicateDetected] = useState(false);
  const PRIMARY_TAB_KEY = `payment_primary_tab_${props?.id}`;
  // Unique ID per tab
  const tabId = React.useRef(
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? (crypto as any).randomUUID()
      : Math.random().toString(36).substring(2) + Date.now().toString()
  ).current;

  // Lock functions
  const checkPrimaryOwnership = (): boolean => {
    const raw = localStorage.getItem(PRIMARY_TAB_KEY);
    if (!raw) return true;

    try {
      const { tabId: existingId, timestamp } = JSON.parse(raw);
      const now = Date.now();
      const isExpired = now - timestamp > 10 * 60 * 1000; // 10-minute expiry
      return existingId === tabId || isExpired;
    } catch {
      return true;
    }
  };

  const setPrimaryLock = () => {
    localStorage.setItem(
      PRIMARY_TAB_KEY,
      JSON.stringify({ tabId, timestamp: Date.now() })
    );
  };

  const releaseLock = () => {
    const raw = localStorage.getItem(PRIMARY_TAB_KEY);
    if (raw) {
      const { tabId: existingId } = JSON.parse(raw);
      if (existingId === tabId) {
        localStorage.removeItem(PRIMARY_TAB_KEY);
      }
    }
  };

  // Set or block tab on first mount
  useEffect(() => {
    const canBecomePrimary = checkPrimaryOwnership();
    if (canBecomePrimary) {
      setIsPrimaryTab(true);
      setPrimaryLock();
    } else {
      setIsPrimaryTab(false);
      setDuplicateDetected(true);
    }

    // Clean up on tab close or redirect
    window.addEventListener('beforeunload', releaseLock);
    return () => window.removeEventListener('beforeunload', releaseLock);
  }, [tabId]);

  // Optional: Real-time tab updates via BroadcastChannel
  useEffect(() => {
    const channel = new BroadcastChannel('payment-session');

    const handleUnload = () => {
      releaseLock();
      channel.postMessage({ type: 'lock-released', tabId });
    };

    channel.onmessage = (e) => {
      const { type, tabId: senderId } = e.data || {};
      if (type === 'lock-released') {
        const canTakeLock = checkPrimaryOwnership();
        if (canTakeLock && !isPrimaryTab) {
          setIsPrimaryTab(true);
          setDuplicateDetected(false);
          setPrimaryLock();
        }
      }
    };

    window.addEventListener('beforeunload', handleUnload);
    return () => {
      window.removeEventListener('beforeunload', handleUnload);
      channel.close();
    };
  }, [tabId, isPrimaryTab]);

  // Handle postMessage from iframe
  const onMessage = useCallback(
    (message: MessageEvent) => {
      if (
        message.data &&
        message.data.type === MessageType.PaymentInformation &&
        props.onCompletePayment
      ) {
        props.onCompletePayment(message.data);
      }
    },
    [props]
  );

  useEffect(() => {
    window.addEventListener('message', onMessage, false);
    return () => window.removeEventListener('message', onMessage);
  }, [onMessage]);

  // Block UI if duplicate detected
  if (duplicateDetected) {
    return (
      <h3 style={{ color: 'red', textAlign: 'center' }}>
        Another tab is already handling this payment. Please close this tab.
      </h3>
    );
  }

  // Proceed with payment iframe
  return <ReactIFrame {...props} target="_top" />;
};

export default PaymentIFrame;
